const DIGIT_WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
] as const;

const TEEN_WORDS = [
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
] as const;

const TENS_WORDS = [
  "",
  "",
  "twenty",
  "thirty",
  "forty",
  "fifty",
  "sixty",
  "seventy",
  "eighty",
  "ninety",
] as const;

function speakIntBelow1000(value: number): string {
  if (value === 0) return "";
  if (value < 10) return DIGIT_WORDS[value]!;
  if (value < 20) return TEEN_WORDS[value - 10]!;
  const tens = Math.floor(value / 10);
  const ones = value % 10;
  const tensWord = TENS_WORDS[tens] ?? "";
  if (ones === 0) return tensWord;
  return `${tensWord} ${DIGIT_WORDS[ones]!}`;
}

function speakIntBelow1Million(value: number): string {
  if (value === 0) return "zero";
  const thousands = Math.floor(value / 1000);
  const remainder = value % 1000;
  const parts: string[] = [];
  if (thousands > 0) {
    parts.push(`${speakIntBelow1000(thousands)} thousand`);
  }
  const tail = speakIntBelow1000(remainder);
  if (tail !== "") parts.push(tail);
  return parts.join(" ");
}

/** Spoken quantity for floor receive (e.g. 10000 -> "ten thousand"). */
export function buildReceiveQuantitySpeechText(quantity: number): string {
  const value = Math.trunc(quantity);
  if (!Number.isFinite(value) || value < 0) return "zero";
  if (value < 1_000_000) {
    return speakIntBelow1Million(value);
  }
  const millions = Math.floor(value / 1_000_000);
  const remainder = value % 1_000_000;
  const head = `${speakIntBelow1000(millions)} million`;
  if (remainder === 0) return head;
  return `${head} ${speakIntBelow1Million(remainder)}`;
}

/** Spoken SKU for floor receive (e.g. CS-2488 -> "c s two four eight eight"). */
export function buildReceiveSkuSpeechText(sku: string): string {
  const parts: string[] = [];
  for (const char of sku.trim()) {
    if (char === "-" || char === "_" || char === " " || char === ".") continue;
    const lower = char.toLowerCase();
    if (lower >= "a" && lower <= "z") {
      parts.push(lower);
      continue;
    }
    if (char >= "0" && char <= "9") {
      parts.push(DIGIT_WORDS[Number(char)]!);
    }
  }
  return parts.join(" ");
}

export function parseReceiveQuantityScan(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const digitsOnly = trimmed.replace(/,/g, "");
  if (!/^\d+$/.test(digitsOnly)) return null;
  const parsed = Number.parseInt(digitsOnly, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

let receiveFloorSpeechVoicesHooked = false;

/** Warm voice list on tablet Chrome / WebView (first speak is often silent until voices load). */
export function primeReceiveFloorSpeechSynthesis(): void {
  if (typeof window === "undefined") return;
  const synthesis = window.speechSynthesis;
  if (synthesis == null) return;
  synthesis.getVoices();
  if (receiveFloorSpeechVoicesHooked) return;
  receiveFloorSpeechVoicesHooked = true;
  synthesis.addEventListener("voiceschanged", () => {
    synthesis.getVoices();
  });
}

function pickReceiveFloorEnglishVoice(
  synthesis: SpeechSynthesis,
): SpeechSynthesisVoice | undefined {
  const voices = synthesis.getVoices();
  if (voices.length === 0) return undefined;
  return (
    voices.find((voice) => voice.lang === "en-US" && voice.localService)
    ?? voices.find((voice) => voice.lang.startsWith("en"))
    ?? voices[0]
  );
}

function buildReceiveFloorUtterance(
  synthesis: SpeechSynthesis,
  text: string,
): SpeechSynthesisUtterance {
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = "en-US";
  utterance.rate = 1.05;
  utterance.volume = 1;
  const voice = pickReceiveFloorEnglishVoice(synthesis);
  if (voice != null) {
    utterance.voice = voice;
  }
  return utterance;
}

function resumeReceiveFloorSpeech(synthesis: SpeechSynthesis): void {
  try {
    if (synthesis.paused) {
      synthesis.resume();
    }
  } catch {
    /* ignore */
  }
}

function speakUtteranceNow(synthesis: SpeechSynthesis, utterance: SpeechSynthesisUtterance): void {
  resumeReceiveFloorSpeech(synthesis);
  synthesis.speak(utterance);
}

/**
 * Chromium (including many floor tablets) drops speak() when it immediately follows cancel(),
 * and the first utterance is often silent until voices load. Retry once when still idle.
 */
function speakReceiveFloorUtterance(synthesis: SpeechSynthesis, text: string): void {
  const utterance = buildReceiveFloorUtterance(synthesis, text);
  const start = () => {
    speakUtteranceNow(synthesis, utterance);
    window.setTimeout(() => {
      if (synthesis.speaking || synthesis.pending) return;
      speakUtteranceNow(synthesis, utterance);
    }, 120);
  };

  const voices = synthesis.getVoices();
  if (voices.length === 0) {
    synthesis.addEventListener(
      "voiceschanged",
      () => {
        start();
      },
      { once: true },
    );
    return;
  }

  if (synthesis.speaking || synthesis.pending) {
    synthesis.cancel();
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(start);
    });
    return;
  }

  start();
}

export function speakReceiveFloorText(text: string): void {
  if (typeof window === "undefined" || text.trim() === "") return;
  const synthesis = window.speechSynthesis;
  if (synthesis == null) return;
  primeReceiveFloorSpeechSynthesis();
  speakReceiveFloorUtterance(synthesis, text);
}

export function speakReceiveSku(sku: string): void {
  const text = buildReceiveSkuSpeechText(sku);
  if (text !== "") speakReceiveFloorText(text);
}

export function speakReceiveQuantity(quantity: number): void {
  speakReceiveFloorText(buildReceiveQuantitySpeechText(quantity));
}
